import React, { useEffect, useMemo, useState } from 'react';
import dayjs from 'dayjs';
import {
  getCampaignOverview,
  getCampaignFunnelByCampaign,
  getCampaignFunnelByCategory,
  getCampaignAcquisitionByCampaign,
  getCampaignAcquisitionByCategory
} from '../api';
import {
  Box, Grid, Paper, Typography, Chip, Button,
  FormControl, InputLabel, Select, MenuItem
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { Bar, Line } from 'react-chartjs-2';
import { useTheme } from '@mui/material/styles';
import PageHeader from './common/PageHeader';
import StatCard from './common/StatCard';

function toBreakdown(rows = []) {
  // Group flexible gender labels into buckets
  const g = rows.reduce((acc, r) => {
    const v = String(r.gender || '').toLowerCase();
    const key =
      v.startsWith('m') ? 'Male' :
      v.startsWith('f') ? 'Female' :
      v.includes('non') ? 'Non-binary' : 'Other';
    acc[key] = (acc[key] || 0) + Number(r.cnt || 0);
    return acc;
  }, {});
  return Object.entries(g).map(([label, value]) => ({ label, value }));
}

export default function CampaignsReport() {
  const theme = useTheme();
  const [startDate, setStartDate] = useState(dayjs().subtract(29,'day'));
  const [endDate, setEndDate]     = useState(dayjs());
  const [overview, setOverview]   = useState(null);
  const [loading, setLoading]     = useState(true);
  const [error, setError]         = useState(null);

  const [selectedCategory, setSelectedCategory] = useState('none'); // 'none' means no funnel selected
  const [selectedCampaign, setSelectedCampaign] = useState('none');

  const [funnel, setFunnel] = useState(null);
  const [funnelTitle, setFunnelTitle] = useState('');
  const [acq, setAcq] = useState(null);
  const [acqTitle, setAcqTitle] = useState('');

  // --- NEW: table sort state ---
  const [catSort, setCatSort]   = useState({ by: 'totalUsers', dir: 'desc' }); // default = Signups desc
  const [campSort, setCampSort] = useState({ by: 'totalUsers', dir: 'desc' }); // default = Signups desc

  // --- NEW: generic helpers for sorting ---
  const sortIcon = (sort, key) => {
    if (sort.by !== key) return '↕';
    return sort.dir === 'asc' ? '▲' : '▼';
  };

  const toggleSort = (setSort, key) => {
    setSort(prev => ({
      by:  key,
      dir: prev.by === key && prev.dir === 'asc' ? 'desc' : 'asc'
    }));
  };

  // derive field values by key (handles strings, numbers, computed rate, and "label")
  const getField = (row, key) => {
    if (key === 'rate') {
      const t = Number(row?.totalUsers || 0);
      const c = Number(row?.completedApproved || 0);
      return t > 0 ? (c / t) : 0;
    }
    if (key === 'label') {
      // Campaigns table first column label
      return row?.campaignReferrerUsername
        ? `${row.campaignReferrerUsername} (ID ${row.campaignUserId})`
        : `Campaign ID ${row.campaignId || '—'}`;
    }
    // fallback direct property
    return row?.[key];
  };

  const sortRows = (rows, sort) => {
    const { by, dir } = sort;
    const mult = dir === 'asc' ? 1 : -1;
    return [...(rows || [])].sort((a, b) => {
      const av = getField(a, by);
      const bv = getField(b, by);
      const aNum = typeof av === 'number';
      const bNum = typeof bv === 'number';
      if (aNum && bNum) {
        const d = (av - bv);
        if (d !== 0) return d * mult;
        // stable-ish tie-breaker
        return String(getField(a, 'categoryName') || getField(a, 'label') || '')
          .localeCompare(String(getField(b, 'categoryName') || getField(b, 'label') || '')) * mult;
      }
      return String(av ?? '').localeCompare(String(bv ?? '')) * mult;
    });
  };

  // (removed shared header style per request; using minimal inline styles on <th>)

  const params = useMemo(() => ({
    startDate: startDate.format('YYYY-MM-DD'),
    endDate:   endDate.format('YYYY-MM-DD')
  }), [startDate, endDate]);

  const fetchOverview = async () => {
    try {
      setLoading(true);
      const data = await getCampaignOverview(params);
      setOverview(data);
      setError(null);
    } catch (e) {
      setError('Failed to load Campaigns overview');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchOverview(); /* eslint-disable-next-line */ }, []);

  const applyDates = () => fetchOverview();

  const loadCategoryFunnel = async (categoryRow) => {
    setSelectedCampaign('none');
    setSelectedCategory(String(categoryRow.categoryId));
    setFunnelTitle(`Funnel — Category: ${categoryRow.categoryName}`);
    const data = await getCampaignFunnelByCategory(categoryRow.categoryId);
    setFunnel(data);
  };

  const loadCampaignFunnel = async (campaignRow) => {
    if (!campaignRow?.campaignId || campaignRow.campaignId <= 0) return;
    try {
      setSelectedCategory('none');
      setSelectedCampaign(String(campaignRow.campaignId));
      const label = campaignRow.campaignReferrerUsername
        ? `${campaignRow.campaignReferrerUsername} (ID ${campaignRow.campaignUserId})`
        : `Campaign ID ${campaignRow.campaignId}`;
      setFunnelTitle(`Funnel — Campaign: ${label}`);
      const data = await getCampaignFunnelByCampaign(campaignRow.campaignId);
      setFunnel(data);
    } catch {
      setFunnel({ total: 0, stages: [] });
    }
  };

  const loadCategoryAcquisition = async (categoryRow) => {
    setSelectedCampaign('none');
    setSelectedCategory(String(categoryRow.categoryId));
    setAcqTitle(`Acquisition — Category: ${categoryRow.categoryName}`);
    const resp = await getCampaignAcquisitionByCategory(categoryRow.categoryId, params);
    setAcq(resp?.data || []);
  };

  const loadCampaignAcquisition = async (campaignRow) => {
    if (!campaignRow?.campaignId || campaignRow.campaignId <= 0) return;
    setSelectedCategory('none');
    setSelectedCampaign(String(campaignRow.campaignId));
    const label = campaignRow.campaignReferrerUsername
      ? `${campaignRow.campaignReferrerUsername} (ID ${campaignRow.campaignUserId})`
      : `Campaign ID ${campaignRow.campaignId}`;
    setAcqTitle(`Acquisition — Campaign: ${label}`);
    const resp = await getCampaignAcquisitionByCampaign(campaignRow.campaignId, params);
    setAcq(resp?.data || []);
  };

  const completionRate = useMemo(() => {
    if (!overview) return 0;
    const tot = overview.totalUsers || 0;
    const done = overview.completedApproved || 0;
    return tot > 0 ? Math.round(1000 * (done / tot)) / 10 : 0;
  }, [overview]);

  // --- NEW: memoized sorted rows ---
  const catRows = useMemo(() => {
    const src = overview?.byCategory || [];
    return sortRows(src, catSort);
  }, [overview, catSort]);

  const campRows = useMemo(() => {
    const src = overview?.byCampaign || [];
    return sortRows(src, campSort);
  }, [overview, campSort]);

  const funnelChart = useMemo(() => {
    if (!funnel?.stages?.length) return null;
    const labels = funnel.stages.map(s => s.stage);
    const counts = funnel.stages.map(s => s.count);
    return {
      data: {
        labels,
        datasets: [{
          label: 'Users',
          data: counts,
          backgroundColor: theme.palette.primary.main,
          borderColor: theme.palette.primary.main
        }]
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        indexAxis: 'y',
        plugins: {
          legend: { display: false },
          datalabels: {
            anchor: 'end', align: 'right', offset: 4, clamp: true, clip: false,
            formatter: (v) => v
          }
        },
        scales: { x: { beginAtZero: true, ticks:{ precision:0 } } }
      }
    };
  }, [funnel]);

  const acqChart = useMemo(() => {
    if (!Array.isArray(acq) || !acq?.length) return null;
    return {
      data: {
        labels: acq.map(r => r.date),
        datasets: [
          {
            label: 'Completed + Approved',
            data: acq.map(r => r.completedApproved),
            borderColor: '#4CAF50',
            tension: 0.25,
            pointRadius: 0,
            fill: false,
          },
          {
            label: 'All signups',
            data: acq.map(r => r.totalUsers),
            borderColor: theme.palette.primary.main,
            backgroundColor: 'rgba(149,62,241,0.12)',
            tension: 0.25,
            pointRadius: 0,
            fill: true,
          }
        ]
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        plugins: { legend: { position: 'top' } },
        scales: { y: { beginAtZero: true, ticks: { precision: 0 } } }
      }
    };
  }, [acq]);

  return (
    <Box>
      <PageHeader
        title="Campaigns"
        actions={(
          <Paper sx={{ p:1.25, display:'flex', gap:1, alignItems:'center', flexWrap:'wrap' }}>
            <DatePicker label="Start" value={startDate} onChange={(d)=>d && setStartDate(d)} slotProps={{ textField:{ size:'small' } }} />
            <DatePicker label="End"   value={endDate}   onChange={(d)=>d && setEndDate(d)}   slotProps={{ textField:{ size:'small' } }} />
            <Button variant="outlined" onClick={applyDates}>Apply</Button>
            {overview && (
              <Chip size="small" label={`Window: ${params.startDate} → ${params.endDate}`} />
            )}
          </Paper>
        )}
      />

      {loading && !overview && (
        <Paper sx={{ p: 3, textAlign:'center' }}><Typography>Loading…</Typography></Paper>
      )}

      {error && !overview && (
        <Paper sx={{ p: 3, textAlign:'center' }}><Typography color="error">{error}</Typography></Paper>
      )}

      {overview && (
        <>
          {/* KPI row */}
          <Grid container spacing={3} sx={{ mb: 2 }}>
            <Grid item xs={12} md={3}>
              <StatCard
                title="Campaign signups"
                value={overview.totalUsers}
                breakdown={toBreakdown(overview.byGenderAll)}
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <StatCard
                title="Completed + approved"
                value={overview.completedApproved}
                breakdown={toBreakdown(overview.byGenderCompletedApproved)}
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <StatCard
                title="Completion rate"
                value={`${completionRate}%`}
                color="secondary"
                helpText="(Completed+Approved) ÷ Campaign signups"
              />
            </Grid>
          </Grid>

          {/* Category table (click to load funnel) */}
          <Paper sx={{ p:2, mb:2 }}>
            <Typography variant="h6" sx={{ mb:1 }}>Categories</Typography>
            {overview.byCategory?.length ? (
              <Box sx={{ overflowX:'auto' }}>
                <table style={{ width:'100%', borderCollapse:'collapse' }}>
                  <thead>
                    <tr>
                      <th
                        align="left"
                        style={{ cursor:'pointer' }}
                        onClick={() => toggleSort(setCatSort, 'categoryName')}
                        title="Sort by Category"
                      >
                        Category&nbsp;{sortIcon(catSort, 'categoryName')}
                      </th>
                      <th
                        align="right"
                        style={{ cursor:'pointer' }}
                        onClick={() => toggleSort(setCatSort, 'totalUsers')}
                        title="Sort by Signups"
                      >
                        Signups&nbsp;{sortIcon(catSort, 'totalUsers')}
                      </th>
                      <th
                        align="right"
                        style={{ cursor:'pointer' }}
                        onClick={() => toggleSort(setCatSort, 'completedApproved')}
                        title="Sort by Completed+Approved"
                      >
                        Completed+Approved&nbsp;{sortIcon(catSort, 'completedApproved')}
                      </th>
                      <th
                        align="right"
                        style={{ cursor:'pointer' }}
                        onClick={() => toggleSort(setCatSort, 'rate')}
                        title="Sort by Completion Rate"
                      >
                        Rate&nbsp;{sortIcon(catSort, 'rate')}
                      </th>
                      <th />
                    </tr>
                  </thead>
                  <tbody>
                    {catRows.map((r) => {
                      const rate = r.totalUsers ? Math.round(1000 * (r.completedApproved / r.totalUsers))/10 : 0;
                      return (
                        <tr key={String(r.categoryId)}>
                          <td style={{ padding:'6px 4px' }}>{r.categoryName}</td>
                          <td style={{ padding:'6px 4px' }} align="right">{r.totalUsers}</td>
                          <td style={{ padding:'6px 4px' }} align="right">{r.completedApproved}</td>
                          <td style={{ padding:'6px 4px' }} align="right">{rate}%</td>
                          <td style={{ padding:'6px 4px' }} align="right">
                            <Button size="small" variant="outlined" onClick={() => loadCategoryFunnel(r)}>Load funnel</Button>
                            <Button size="small" sx={{ ml: 1 }} variant="outlined" onClick={() => loadCategoryAcquisition(r)}>Load acquisition</Button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </Box>
            ) : <Typography variant="body2" color="text.secondary">No category data</Typography>}
          </Paper>

          {/* Campaign table (click to load funnel) */}
          <Paper sx={{ p:2, mb:2 }}>
            <Typography variant="h6" sx={{ mb:1 }}>Campaigns</Typography>
            {overview.byCampaign?.length ? (
              <Box sx={{ overflowX:'auto' }}>
                <table style={{ width:'100%', borderCollapse:'collapse' }}>
                  <thead>
                    <tr>
                      <th
                        align="left"
                        style={{ cursor:'pointer' }}
                        onClick={() => toggleSort(setCampSort, 'label')}
                        title="Sort by Referrer"
                      >
                        Referrer (is_campaign user)&nbsp;{sortIcon(campSort, 'label')}
                      </th>
                      <th
                        align="left"
                        style={{ cursor:'pointer' }}
                        onClick={() => toggleSort(setCampSort, 'campaignDescription')}
                        title="Sort by Description"
                      >
                        Description&nbsp;{sortIcon(campSort, 'campaignDescription')}
                      </th>
                      <th
                        align="left"
                        style={{ cursor:'pointer' }}
                        onClick={() => toggleSort(setCampSort, 'categoryName')}
                        title="Sort by Category"
                      >
                        Category&nbsp;{sortIcon(campSort, 'categoryName')}
                      </th>
                      <th
                        align="right"
                        style={{ cursor:'pointer' }}
                        onClick={() => toggleSort(setCampSort, 'totalUsers')}
                        title="Sort by Signups"
                      >
                        Signups&nbsp;{sortIcon(campSort, 'totalUsers')}
                      </th>
                      <th
                        align="right"
                        style={{ cursor:'pointer' }}
                        onClick={() => toggleSort(setCampSort, 'completedApproved')}
                        title="Sort by Completed+Approved"
                      >
                        Completed+Approved&nbsp;{sortIcon(campSort, 'completedApproved')}
                      </th>
                      <th
                        align="right"
                        style={{ cursor:'pointer' }}
                        onClick={() => toggleSort(setCampSort, 'rate')}
                        title="Sort by Completion Rate"
                      >
                        Rate&nbsp;{sortIcon(campSort, 'rate')}
                      </th>
                      <th />
                    </tr>
                  </thead>
                  <tbody>
                    {campRows.map((r, i) => {
                      const rate = r.totalUsers ? Math.round(1000 * (r.completedApproved / r.totalUsers))/10 : 0;
                      const label = r.campaignReferrerUsername
                        ? `${r.campaignReferrerUsername} (ID ${r.campaignUserId})`
                        : `Campaign ID ${r.campaignId || '—'}`;
                      return (
                        <tr key={`${r.campaignId || 0}-${i}`}>
                          <td style={{ padding:'6px 4px' }}>{label}</td>
                          <td style={{ padding:'6px 4px', maxWidth: 360, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>
                            {r.campaignDescription || '—'}
                          </td>
                          <td style={{ padding:'6px 4px' }}>{r.categoryName}</td>
                          <td style={{ padding:'6px 4px' }} align="right">{r.totalUsers}</td>
                          <td style={{ padding:'6px 4px' }} align="right">{r.completedApproved}</td>
                          <td style={{ padding:'6px 4px' }} align="right">{rate}%</td>
                          <td style={{ padding:'6px 4px' }} align="right">
                            <Button
                              size="small"
                              variant="outlined"
                              disabled={!r.campaignId || r.campaignId <= 0}
                              onClick={() => loadCampaignFunnel(r)}
                              title={!r.campaignId || r.campaignId <= 0 ? 'No campaign record for this row' : 'Load funnel'}
                            >
                              Load funnel
                            </Button>
                            <Button
                              size="small"
                              sx={{ ml: 1 }}
                              variant="outlined"
                              disabled={!r.campaignId || r.campaignId <= 0}
                              onClick={() => loadCampaignAcquisition(r)}
                              title={!r.campaignId || r.campaignId <= 0 ? 'No campaign record for this row' : 'Load acquisition'}
                            >
                              Load acquisition
                            </Button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </Box>
            ) : <Typography variant="body2" color="text.secondary">No campaign data</Typography>}
          </Paper>

          {/* Funnel chart (lazy, only when selected) */}
          {funnelChart && (
            <Paper sx={{ p:2, mb:2 }}>
              <Typography variant="h6" sx={{ mb: 1 }}>{funnelTitle}</Typography>
              <Typography variant="caption" sx={{ display:'block', mb: 1 }}>
                Cohort size: {funnel?.total?.toLocaleString?.() || funnel?.total || 0}
              </Typography>
              <Box sx={{ height: 360 }}>
                <Bar data={funnelChart.data} options={funnelChart.options} />
              </Box>
            </Paper>
          )}

          {acqChart && (
            <Paper sx={{ p:2 }}>
              <Typography variant="h6" sx={{ mb: 1 }}>{acqTitle}</Typography>
              <Typography variant="caption" sx={{ display:'block', mb: 1 }}>
                Window: {params.startDate} → {params.endDate}
              </Typography>
              <Box sx={{ height: 360 }}>
                <Line data={acqChart.data} options={acqChart.options} />
              </Box>
            </Paper>
          )}
        </>
      )}
    </Box>
  );
}


